import React, { useRef } from 'react';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { TOrbit } from '../../../../i18n/TModules';
import { positive } from '../../adapters/common';
import { milestoneErrors } from '../../adapters/progress';
import StepPanel from '../../kit/StepPanel';
import { DashedAdd } from '../../kit/controls';
import { TextField } from '../../kit/fields';
import { Line, Words } from '../../kit/layout';
import { IWizardStepProps, newPrize, ProgressLevel } from '../../wizardModel';
import { wz, wzType } from '../../wizardStyles';

const TM = `${TOrbit}.wizard.milestones`;

/** The track's scale before the last level has points. */
const DEFAULT_SCALE = 100;

// 6778:23134: a rail with a fixed start at 0 and a marker per level; the last level ends it.
const Track = styled.div`
  position: relative;
  height: 74px;
  margin: 8px 12px 18px;
`;

const Rail = styled.div`
  position: absolute;
  top: 30px;
  right: 0;
  left: 0;
  height: 4px;
  border-radius: 4px;
  background: linear-gradient(90deg, ${wz.violet} 0%, ${wz.accent} 100%);
`;

const Marker = styled.div<{ $at: number; $draggable: boolean }>`
  position: absolute;
  top: 0;
  left: ${({ $at }) => $at}%;
  display: flex;
  flex-direction: column;
  align-items: center;
  transform: translateX(-50%);
  cursor: ${({ $draggable }) => ($draggable ? 'ew-resize' : 'default')};
  touch-action: none;

  &:focus-visible {
    outline: none;

    > span:nth-child(2) {
      box-shadow: 0 0 0 3px ${wz.accent};
    }
  }
`;

const Value = styled.span`
  color: ${wz.text};
  font-weight: 700;
  ${wzType.label}
`;

const Pin = styled.span`
  width: 16px;
  height: 16px;
  margin-top: 4px;
  border: 2px solid ${wz.accent};
  border-radius: 50%;
  background: ${wz.panel};
`;

const LevelLabel = styled.span`
  margin-top: 8px;
  color: ${wz.secondary};
  white-space: nowrap;
  ${wzType.label}
`;

// 6985:4512: "+ Add milestone" and a red "Remove last", both outlined, 38 tall.
const Controls = styled.div`
  display: flex;
  margin-top: 8px;

  > * + * {
    margin-left: 12px;
  }
`;

const AddMilestone = styled(DashedAdd)`
  min-height: 38px;
  border-style: solid;
`;

const RemoveLast = styled(AddMilestone)`
  color: ${wz.danger};
`;

const LevelRow = styled(Line)`
  > :first-child {
    flex: 0 0 120px;
  }
`;

/**
 * Step 2/5 of Progress. Levels hold absolute campaign points, as the module stores them, so the
 * last level's points set the scale; the other markers drag (or arrow-key) between neighbours.
 */
const MilestoneStep: React.FC<IWizardStepProps> = ({ state, update, next, back }) => {
  const { t } = useMultiLanguageTranslation();
  const trackRef = useRef<HTMLDivElement>(null);
  const { levels } = state;
  const last = levels[levels.length - 1];
  const scale = last !== undefined && positive(last.points) ? Number(last.points) : DEFAULT_SCALE;

  const setLevels = (change: (levels: ProgressLevel[]) => ProgressLevel[]) =>
    update(current => ({ ...current, levels: change(current.levels) }));
  const setPoints = (index: number, points: string) =>
    setLevels(current => current.map((level, at) => (at === index ? { ...level, points } : level)));
  const bounds = (index: number) => ({
    min: index === 0 ? 1 : Number(levels[index - 1].points) + 1,
    max: Number(levels[index + 1]?.points ?? scale) - 1
  });
  const clampTo = (index: number, value: number) => {
    const { min, max } = bounds(index);
    return Math.max(min, Math.min(max, Math.round(value)));
  };

  const drag = (index: number) => (event: React.PointerEvent<HTMLDivElement>) => {
    const track = trackRef.current;
    if (track === null) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    const move = (moveEvent: PointerEvent) => {
      const { left, width } = track.getBoundingClientRect();
      if (width > 0) {
        setPoints(index, String(clampTo(index, ((moveEvent.clientX - left) / width) * scale)));
      }
    };
    const stop = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', stop);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', stop);
  };

  return (
    <StepPanel
      step="milestones"
      narrow
      title={t(`${TOrbit}.wizard.setup.progress`)}
      question={t(`${TM}.question`)}
      errors={milestoneErrors(levels, t)}
      onNext={next}
      onBack={back}
    >
      <Track ref={trackRef} data-testid="orbit-wizard-milestone-track">
        <Rail />
        <Marker $at={0} $draggable={false}>
          <Value>0</Value>
          <Pin />
        </Marker>
        {levels.map((level, index) => {
          const end = index === levels.length - 1;
          const at = positive(level.points)
            ? Math.min(100, (Number(level.points) / scale) * 100)
            : 100;
          const label = t(end ? `${TM}.levelEnd` : `${TM}.level`, { n: index + 1 });
          return (
            <Marker
              // Levels are their position: Level n is always the n-th.
              key={index}
              $at={at}
              $draggable={!end}
              role={end ? undefined : 'slider'}
              tabIndex={end ? undefined : 0}
              aria-label={end ? undefined : t(`${TM}.points`, { n: index + 1 })}
              aria-valuenow={end ? undefined : Number(level.points) || 0}
              aria-valuemin={end ? undefined : bounds(index).min}
              aria-valuemax={end ? undefined : bounds(index).max}
              onPointerDown={end ? undefined : drag(index)}
              onKeyDown={
                end
                  ? undefined
                  : event => {
                      const step = event.shiftKey ? 10 : 1;
                      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
                        event.preventDefault();
                        const current = Number(level.points) || 0;
                        setPoints(
                          index,
                          String(
                            clampTo(index, current + (event.key === 'ArrowRight' ? step : -step))
                          )
                        );
                      }
                    }
              }
              data-testid="orbit-wizard-milestone"
            >
              <Value>{level.points === '' ? '–' : level.points}</Value>
              <Pin />
              <LevelLabel>{label}</LevelLabel>
            </Marker>
          );
        })}
      </Track>
      {levels.map((level, index) => (
        // Levels are their position: Level n is always the n-th.
        <LevelRow key={index}>
          <Words>{t(`${TM}.level`, { n: index + 1 })}</Words>
          <TextField
            label={t(`${TM}.points`, { n: index + 1 })}
            placeholder="0"
            inputMode="numeric"
            value={level.points}
            onChange={points => setPoints(index, points)}
          />
        </LevelRow>
      ))}
      <Controls>
        <AddMilestone
          type="button"
          onClick={() =>
            setLevels(current => {
              const top = current[current.length - 1];
              const points =
                top !== undefined && positive(top.points)
                  ? String(Number(top.points) + DEFAULT_SCALE)
                  : '';
              return [...current, { points, prizes: [newPrize()] }];
            })
          }
        >
          <PlusOutlined aria-hidden="true" />
          <span>{t(`${TM}.add`)}</span>
        </AddMilestone>
        {levels.length > 1 ? (
          <RemoveLast
            type="button"
            onClick={() =>
              update(current => ({
                ...current,
                levels: current.levels.slice(0, -1),
                activeLevel: 0
              }))
            }
          >
            <DeleteOutlined aria-hidden="true" />
            <span>{t(`${TM}.removeLast`)}</span>
          </RemoveLast>
        ) : null}
      </Controls>
    </StepPanel>
  );
};

export default MilestoneStep;
